import { useState, useCallback } from "react";
import { robotsService } from "../services/robotsService";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";

/**
 * Custom hook for robot management
 * Handles all robot CRUD operations and state management
 */
export const useRobotManagement = (isMyRobots = false) => {
  const { authFetch, auth } = useAuth();
  const { notifySuccess, notifyError } = useNotification();

  const [robotsData, setRobotsData] = useState({
    page: 1,
    per_page: 5,
    total: 0,
    results: [],
  });
  const [loading, setLoading] = useState(false);
  const [robotTypes, setRobotTypes] = useState([]);
  const [organizationsList, setOrganizationsList] = useState([]);

  /**
   * Load robots data with pagination
   */
  const loadRobots = useCallback(
    async (page = 1, perPage = 5) => {
      try {
        setLoading(true);
        const response = isMyRobots
          ? await robotsService.getMyRobots(authFetch, page, perPage)
          : await robotsService.getAll(authFetch, page, perPage);

        setRobotsData(response.data);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error.message;
        console.error("Error loading robots:", error);
        notifyError(`Failed to load robots: ${errorMessage}`);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, notifyError, isMyRobots]
  );

  /**
   * Load robot types
   */
  const loadRobotTypes = useCallback(async () => {
    try {
      const response = await robotsService.getRobotTypes(authFetch);
      setRobotTypes(response.data?.results || []);
      return response.data?.results || [];
    } catch (error) {
      console.error("Error loading robot types:", error);
      notifyError("Failed to load robot types");
      return [];
    }
  }, [authFetch, notifyError]);

  /**
   * Create a new robot
   */
  const createRobot = useCallback(
    async (robotData) => {
      try {
        const response = await robotsService.create(authFetch, robotData);
        notifySuccess("Robot created successfully");
        // Reload robots to show the new one
        await loadRobots(robotsData.page, robotsData.per_page);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error.message;
        console.error("Error creating robot:", error);
        notifyError(`Failed to create robot: ${errorMessage}`);
        throw error;
      }
    },
    [authFetch, notifySuccess, notifyError, loadRobots, robotsData.page, robotsData.per_page]
  );

  /**
   * Update an existing robot
   */
  const updateRobot = useCallback(
    async (robotId, updateData) => {
      try {
        const response = await robotsService.update(authFetch, robotId, updateData);
        notifySuccess("Robot updated successfully");
        // Reload robots to show the updates
        await loadRobots(robotsData.page, robotsData.per_page);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error.message;
        console.error("Error updating robot:", error);
        notifyError(`Failed to update robot: ${errorMessage}`);
        throw error;
      }
    },
    [authFetch, notifySuccess, notifyError, loadRobots, robotsData.page, robotsData.per_page]
  );

  /**
   * Delete a robot
   */
  const deleteRobot = useCallback(
    async (robotId) => {
      try {
        await robotsService.delete(authFetch, robotId);
        notifySuccess("Robot deleted successfully");
        // Reload robots after deletion
        await loadRobots(robotsData.page, robotsData.per_page);
        return true;
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error.message;
        console.error("Error deleting robot:", error);
        notifyError(`Failed to delete robot: ${errorMessage}`);
        return false;
      }
    },
    [authFetch, notifySuccess, notifyError, loadRobots, robotsData.page, robotsData.per_page]
  );

  return {
    // State
    robotsData,
    loading,
    robotTypes,
    organizationsList,
    setOrganizationsList,

    // Methods
    loadRobots,
    loadRobotTypes,
    createRobot,
    updateRobot,
    deleteRobot,

    // Pagination helpers
    pagination: {
      current: robotsData.page,
      pageSize: robotsData.per_page,
      total: robotsData.total,
      onChange: (page, pageSize) => loadRobots(page, pageSize),
    },
  };
};
